import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  RegisteredNotificationChannel,
  NotificationEventSubscription,
  NotificationPreferences,
} from '@/types/notification-settings';

export type NotificationSettingsState = {
  preferences: NotificationPreferences | null;
  events: NotificationEventSubscription[];
  channels: RegisteredNotificationChannel[];
  preferencesLoaded: boolean;
  eventsLoaded: boolean;
  channelsLoaded: boolean;
};

export const notificationSettingsInitialState: NotificationSettingsState = {
  preferences: null,
  events: [],
  channels: [],
  preferencesLoaded: false,
  eventsLoaded: false,
  channelsLoaded: false,
};

const slice = createSlice({
  name: 'notificationSettings',
  initialState: notificationSettingsInitialState,
  reducers: {
    resetNotificationSettingsAction: () => notificationSettingsInitialState,
    setNotificationPreferencesAction(
      state,
      { payload }: PayloadAction<NotificationPreferences>,
    ) {
      state.preferences = payload;
      state.preferencesLoaded = true;
    },
    setNotificationEventsAction(
      state,
      { payload }: PayloadAction<NotificationEventSubscription[]>,
    ) {
      state.events = payload;
      state.eventsLoaded = true;
    },
    setNotificationChannelsAction(
      state,
      { payload }: PayloadAction<RegisteredNotificationChannel[]>,
    ) {
      state.channels = payload;
      state.channelsLoaded = true;
    },
    upsertNotificationChannelAction(
      state,
      { payload }: PayloadAction<RegisteredNotificationChannel>,
    ) {
      const index = state.channels.findIndex((channel) => channel.id === payload.id);
      if (index >= 0) state.channels[index] = payload;
      else state.channels.unshift(payload);
      state.channelsLoaded = true;
    },
    removeNotificationChannelAction(state, { payload }: PayloadAction<string>) {
      state.channels = state.channels.filter((channel) => channel.id !== payload);
    },
    setNotificationEventOptOutsAction(
      state,
      { payload }: PayloadAction<string[]>,
    ) {
      const optedOut = new Set(payload);
      state.events = state.events.map((event) => ({
        ...event,
        subscribed: event.category.toLowerCase() === 'security'
          ? true
          : !optedOut.has(event.eventType),
      }));
      state.eventsLoaded = true;
    },
  },
});

export const {
  resetNotificationSettingsAction,
  setNotificationPreferencesAction,
  setNotificationEventsAction,
  setNotificationChannelsAction,
  upsertNotificationChannelAction,
  removeNotificationChannelAction,
  setNotificationEventOptOutsAction,
} = slice.actions;

export default slice.reducer;
